import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message, Alert, Space } from 'antd';
import { ExperimentOutlined, RocketOutlined, SettingOutlined } from '@ant-design/icons';
import WorkflowGenerationLoader from './WorkflowGenerationLoader';
import WorkflowGeneratorErrorBoundary from './WorkflowGeneratorErrorBoundary';
import { adminInstance } from './api/axiosConfig';

const AIWorkflowGeneratorModal = ({ 
  visible, 
  onCancel, 
  onGenerate 
}) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasOpenRouterKey, setHasOpenRouterKey] = useState(false);
  const [isCheckingAPI, setIsCheckingAPI] = useState(true);

  // Check for OpenRouter API key when modal opens
  useEffect(() => {
    if (visible) {
      checkOpenRouterAPI();
    }
  }, [visible]);

  const checkOpenRouterAPI = async () => {
    setIsCheckingAPI(true);
    try {
      const response = await adminInstance.get('/settings');
      setHasOpenRouterKey(!!response.data.openrouter_api_key);
    } catch (error) {
      console.error('Error checking OpenRouter API:', error);
      setHasOpenRouterKey(false);
    } finally {
      setIsCheckingAPI(false);
    }
  };

  const handleGenerate = async () => {
    if (!hasOpenRouterKey) {
      message.error('Please configure OpenRouter API key first');
      return;
    }

    if (!prompt.trim()) {
      message.error('Please enter a workflow description');
      return;
    }

    try {
      setIsGenerating(true);
      await onGenerate(prompt);
      setPrompt('');
    } catch (error) {
      message.error('Failed to generate workflow: ' + error.message);
    } finally {
      setIsGenerating(false);
      onCancel();
    }
  };

  const goToSettings = () => {
    window.location.href = `${window.wpAiWorkflowsSettings.adminUrl}admin.php?page=wp-ai-workflows&action=settings`;
  };

  if (isGenerating) {
    return <WorkflowGenerationLoader />;
  }

  const handleClose = () => {
    setPrompt('');
    onCancel();
  };

  const handleRetry = (savedPrompt) => {
    setIsGenerating(false);
    if (savedPrompt) {
      setPrompt(savedPrompt); // Restore the prompt
    }
  };

  return (
    <WorkflowGeneratorErrorBoundary
      onRetry={handleRetry}
      prompt={prompt} // Pass the current prompt
    >
      {isGenerating ? (
        <WorkflowGenerationLoader />
      ) : (
    <Modal
      title={
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <RocketOutlined style={{ fontSize: 24, marginBottom: 16 }} />
          <h3 style={{ margin: 0 }}>Generate Workflow with AI</h3>
        </div>
      }
      open={visible && !isGenerating}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          onClick={handleGenerate}
          icon={<ExperimentOutlined />}
          disabled={!hasOpenRouterKey}
        >
          Generate Workflow
        </Button>
      ]}
      width={600}
      centered
      destroyOnClose
    >
      {!hasOpenRouterKey && !isCheckingAPI && (
        <Alert
          message="OpenRouter API Required"
          description={
            <Space direction="vertical" size="small">
              <span>
                To use the AI Workflow Generator, you need to configure your OpenRouter API key.
                OpenRouter provides access to various AI models including Claude and GPT-4.
              </span>
              <Button 
                type="primary" 
                icon={<SettingOutlined />}
                onClick={goToSettings}
              >
                Configure API Key
              </Button>
              <a 
                href="https://openrouter.ai/keys" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Get an OpenRouter API key
              </a>
            </Space>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

            <Input.TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={hasOpenRouterKey ? 
              "Write something like: Create a simple blog article generator workflow." : 
              "Please configure OpenRouter API key first"}
            style={{ 
              height: 200, 
              marginBottom: 24,
              fontSize: 16,
              padding: 16,
              resize: 'none'
            }}
            disabled={!hasOpenRouterKey}
            autoFocus={hasOpenRouterKey}
          />
    </Modal>
          )}
    {isGenerating && <WorkflowGenerationLoader />}
    </WorkflowGeneratorErrorBoundary>
  );
};

export default AIWorkflowGeneratorModal;